import { useState } from 'react'
import { X, Mail } from 'lucide-react'
import type { BookingRecord } from '../../../types'
import { useConfirmDialog } from '../../../context/ConfirmDialogContext'

type NotifyCustomerModalProps = {
  isOpen: boolean
  onClose: () => void
  booking: BookingRecord | null
  onSend: (subject: string, message: string) => Promise<boolean>
}

export const NotifyCustomerModal = ({
  isOpen,
  onClose,
  booking,
  onSend,
}: NotifyCustomerModalProps) => {
  const { showSuccess } = useConfirmDialog()

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen || !booking) return null

  const handleClose = () => {
    if (isSending) return

    setSubject('')
    setMessage('')
    setError(null)
    onClose()
  }

  const handleSend = async () => {
    const trimmedSubject = subject.trim()
    const trimmedMessage = message.trim()

    if (!trimmedSubject) {
      setError('Please enter a subject.')
      return
    }

    if (!trimmedMessage) {
      setError('Please enter a message.')
      return
    }

    setError(null)
    setIsSending(true)

    try {
      const success = await onSend(trimmedSubject, trimmedMessage)

      if (!success) {
        setError('Failed to send the notification. Please try again.')
        return
      }

      setSubject('')
      setMessage('')
      setError(null)
      onClose()

      await showSuccess({
        title: 'Notification Sent',
        message: `Your message has been sent to ${booking.customer.email}.`,
      })
    } catch {
      setError('Failed to send the notification. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-[620px] rounded-[16px] bg-[#FAFAFA] p-6 shadow-lg">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#EEEEEE]">
              <Mail size={19} className="text-black" />
            </div>

            <div>
              <h2 className="font-host text-[20px] font-semibold text-black">
                Notify Customer
              </h2>

              <p className="font-host text-[13px] text-[#757575]">
                Send an email notification to the customer
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-black text-white transition-colors hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        {/* Recipient */}
        <div className="mb-5 rounded-[10px] border border-[#E0E0E0] bg-white px-4 py-3">
          <div className="mb-1 font-host text-[12px] font-medium text-[#757575]">
            Recipient
          </div>

          <div className="font-host text-[14px] font-medium text-black">
            {booking.customer.email}
          </div>
        </div>

        {/* Subject */}
        <div className="mb-5">
          <label
            htmlFor="notify-subject"
            className="mb-2 block font-host text-[14px] font-medium text-black"
          >
            Subject
          </label>

          <input
            id="notify-subject"
            type="text"
            value={subject}
            onChange={(event) => {
              setSubject(event.target.value)
              setError(null)
            }}
            placeholder="Enter email subject"
            disabled={isSending}
            className="h-[46px] w-full rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-black outline-none transition-colors placeholder:text-[#A0A0A0] focus:border-black disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
          />
        </div>

        {/* Message */}
        <div className="mb-5">
          <label
            htmlFor="notify-message"
            className="mb-2 block font-host text-[14px] font-medium text-black"
          >
            Message
          </label>

          <textarea
            id="notify-message"
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
              setError(null)
            }}
            placeholder="Write your message to the customer..."
            rows={7}
            disabled={isSending}
            className="w-full resize-none rounded-[8px] border border-[#E0E0E0] bg-white px-4 py-3 font-host text-[14px] leading-[1.6] text-black outline-none transition-colors placeholder:text-[#A0A0A0] focus:border-black disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 font-host text-[13px] text-red-600">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            className="h-[44px] rounded-[8px] border border-[#D9D9D9] bg-white px-5 font-host text-[14px] font-medium text-black transition-colors hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className="flex h-[44px] min-w-[150px] items-center justify-center rounded-[8px] bg-black px-5 font-host text-[14px] font-medium text-white transition-colors hover:bg-black/85 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSending ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>
    </div>
  )
}
